/**
 * Email validation utility
 * Validates email format according to RFC 5322 standards
 */

/**
 * Validates email format
 * @param {string} email - The email address to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export function isValidEmail(email) {
  if (!email || typeof email !== 'string') {
    return false;
  }

  // RFC 5322 compliant email validation regex
  // This regex handles most common email formats including:
  // - Standard emails: user@example.com
  // - Emails with dots: user.name@example.com
  // - Emails with plus: user+tag@example.com
  // - Emails with hyphens: user-name@example-domain.com
  // - Subdomain emails: user@mail.example.com
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  return emailRegex.test(email.trim());
}

/**
 * Gets email validation error message
 * @param {string} email - The email address to validate
 * @returns {string|null} - Error message if invalid, null if valid
 */
export function getEmailError(email) {
  if (!email || email.trim() === '') {
    return 'Email is required';
  }
  
  if (!isValidEmail(email)) {
    return 'Please enter a valid email address';
  }
  
  return null;
}

/**
 * Validates email on blur (when user leaves the field)
 * @param {string} email - The email address to validate
 * @param {function} setError - Function to set error state
 */
export function validateEmailOnBlur(email, setError) {
  const error = getEmailError(email);
  setError(error);
}

/**
 * Validates email on input (as user types)
 * Only shows error if email is not empty and invalid
 * @param {string} email - The email address to validate
 * @param {function} setError - Function to set error state
 */
export function validateEmailOnInput(email, setError) {
  if (!email || email.trim() === '') {
    setError(null);
    return;
  }
  
  if (!isValidEmail(email)) {
    setError('Please enter a valid email address');
  } else {
    setError(null);
  }
}

export default {
  isValidEmail,
  getEmailError,
  validateEmailOnBlur,
  validateEmailOnInput
};
